// Interpreta o texto lido (OCR) de um print de tela com os dados de uma pessoa (ex.: card do proprietário/adquirente
// no portal do DETRAN) e extrai os campos do cadastro de cliente. Funções puras, sem dependências.
//
// O print costuma vir assim (rótulo à esquerda, valor à direita, o endereço pode quebrar em duas linhas):
//   CLAUDIA DE AVILA BRAGA
//   CPF: 039.584.800-80
//   Endereço        Rua Joaquim Paulo De Freitas, 140 Casa -
//                   Triangulo
//   Município / UF  Cangucu / RS
//   CEP             96600-000
//   E-mail          fulano@gmail.com
//   Telefone        53 98425 1429

const UFS = ['AC', 'AL', 'AP', 'AM', 'BA', 'CE', 'DF', 'ES', 'GO', 'MA', 'MT', 'MS', 'MG', 'PA', 'PB', 'PR', 'PE', 'PI',
  'RJ', 'RN', 'RS', 'RO', 'RR', 'SC', 'SP', 'SE', 'TO'];

// Rótulos conhecidos: marcam onde termina o valor do rótulo anterior (o endereço pode ocupar mais de uma linha)
const ROTULOS = {
  nome: /^(NOME|PROPRIET[ÁA]RIO|ADQUIRENTE)\b\s*:?/i,
  cpf: /^CPF\b\s*:?/i,
  cnpj: /^CNPJ\b\s*:?/i,
  cpfCnpj: /^CPF\s*\/\s*CNPJ\b\s*:?/i,
  endereco: /^(ENDERE[ÇC]O|LOGRADOURO)\b\s*:?/i,
  bairro: /^BAIRRO\b\s*:?/i,
  municipio: /^(MUNIC[ÍI]PIO|CIDADE)(\s*\/\s*UF)?\b\s*:?/i,
  uf: /^UF\b\s*:?/i,
  cep: /^CEP\b\s*:?/i,
  email: /^E\s*-?\s*MAIL\b\s*:?/i,
  telefone: /^(TELEFONE|CELULAR|FONE|TEL\.?)\b\s*:?/i,
  outros: /^(DATA\b|RG\b|IDENTIDADE\b|NASCIMENTO\b)/i,
};

const ehRotulo = (linha) => Object.values(ROTULOS).some((r) => r.test(linha));

export function cpfValido(valor) {
  const c = (valor || '').replace(/\D/g, '');
  if (c.length !== 11 || /^(\d)\1+$/.test(c)) return false;
  const dv = (n) => {
    const soma = c.slice(0, n).split('').reduce((acc, d, i) => acc + Number(d) * (n + 1 - i), 0);
    const resto = (soma * 10) % 11;
    return resto === 10 ? 0 : resto;
  };
  return dv(9) === Number(c[9]) && dv(10) === Number(c[10]);
}

export function cnpjValido(valor) {
  const c = (valor || '').replace(/\D/g, '');
  if (c.length !== 14 || /^(\d)\1+$/.test(c)) return false;
  const dv = (n) => {
    const pesos = n === 12 ? [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2] : [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
    const resto = pesos.reduce((acc, peso, i) => acc + Number(c[i]) * peso, 0) % 11;
    return resto < 2 ? 0 : 11 - resto;
  };
  return dv(12) === Number(c[12]) && dv(13) === Number(c[13]);
}

// Valor de um rótulo: o resto da linha do rótulo; com `continuar`, junta as linhas seguintes até o próximo rótulo
function valorDoRotulo(linhas, regexRotulo, continuar = false) {
  for (let i = 0; i < linhas.length; i++) {
    const achado = linhas[i].match(regexRotulo);
    if (!achado || achado.index !== 0) continue;
    const partes = [linhas[i].slice(achado[0].length).trim()];
    for (let j = i + 1; j < linhas.length && (continuar || !partes[0]) && !ehRotulo(linhas[j]); j++) {
      partes.push(linhas[j]);
      if (!continuar) break;
    }
    return partes.filter(Boolean).join(' ').replace(/\s+/g, ' ').trim();
  }
  return '';
}

// Letras que o OCR troca dentro de números
const corrigirDigitos = (t) => (t || '').replace(/[Oo]/g, '0').replace(/[lI|]/g, '1');

function extrairDocumento(linhas, texto) {
  const doRotulo = corrigirDigitos(valorDoRotulo(linhas, ROTULOS.cpfCnpj) || valorDoRotulo(linhas, ROTULOS.cnpj)
    || valorDoRotulo(linhas, ROTULOS.cpf));
  const candidatos = [
    ...(doRotulo.match(/\d[\d.\-/ ]{9,20}\d/g) || []),
    ...(texto.match(/\b\d{2}\.?\d{3}\.?\d{3}\/?\d{4}-?\d{2}\b/g) || []), // CNPJ formatado em qualquer lugar
    ...(texto.match(/\b\d{3}\.\d{3}\.\d{3}-\d{2}\b/g) || []), // CPF formatado em qualquer lugar
  ].map((c) => c.replace(/\D/g, ''));
  const valido = candidatos.find((c) => (c.length === 11 && cpfValido(c)) || (c.length === 14 && cnpjValido(c)));
  if (valido) return { numero: valido, duvida: false };
  const qualquer = candidatos.find((c) => c.length === 11 || c.length === 14);
  return { numero: qualquer || '', duvida: !!qualquer };
}

// Nome: depois do rótulo "Nome", ou a primeira linha só de letras antes do CPF/CNPJ (o destaque do card)
function extrairNome(linhas) {
  const doRotulo = valorDoRotulo(linhas, ROTULOS.nome);
  if (doRotulo) return doRotulo;
  const pareceNome = (l) => /^[A-Za-zÀ-ÿ'´`. ]{5,}$/.test(l) && l.trim().split(/\s+/).length >= 2 && !ehRotulo(l);
  const indiceDocumento = linhas.findIndex((l) => ROTULOS.cpf.test(l) || ROTULOS.cnpj.test(l) || ROTULOS.cpfCnpj.test(l));
  const antes = indiceDocumento > 0 ? linhas.slice(0, indiceDocumento) : linhas;
  return (antes.find(pareceNome) || '').replace(/\s+/g, ' ').trim();
}

// "Rua Joaquim Paulo De Freitas, 140 Casa - Triangulo" → logradouro, número, complemento e bairro.
// O que não der para separar com segurança fica no logradouro, para o usuário ajustar.
export function separarEndereco(endereco) {
  const texto = (endereco || '').replace(/\s+/g, ' ').replace(/\s*-\s*$/, '').trim();
  if (!texto) return {};
  const partes = texto.match(/^(.+?),\s*(\d+[A-Za-z]?|S\s*\/?\s*N)\b\s*(.*)$/i);
  if (!partes) return { logradouro: texto };

  const resultado = { logradouro: partes[1].trim(), numero: partes[2].replace(/\s+/g, '').toUpperCase() };
  const resto = partes[3].replace(/^[,\s]+/, '').trim();
  const traco = resto.lastIndexOf(' - ');
  if (resto.startsWith('-')) {
    resultado.bairro = resto.replace(/^-\s*/, '').trim();
  } else if (traco >= 0) {
    resultado.complemento = resto.slice(0, traco).trim();
    resultado.bairro = resto.slice(traco + 3).trim();
  } else if (resto) {
    resultado.complemento = resto;
  }
  Object.keys(resultado).forEach((campo) => { if (!resultado[campo]) delete resultado[campo]; });
  return resultado;
}

// O OCR costuma ler o "S" da UF como "$" ou "5" e o "O" como "0"
function corrigirUf(valor) {
  const uf = (valor || '').toUpperCase().replace(/\$/g, 'S').replace(/5/g, 'S').replace(/0/g, 'O').replace(/[^A-Z]/g, '');
  return UFS.includes(uf) ? uf : '';
}

function extrairCidadeUf(linhas) {
  const municipio = valorDoRotulo(linhas, ROTULOS.municipio);
  const ufSeparada = corrigirUf(valorDoRotulo(linhas, ROTULOS.uf));
  const divisao = municipio.match(/^(.*?)\s*[/-]\s*(\S{2})\s*$/);
  if (divisao) {
    const uf = corrigirUf(divisao[2]);
    if (uf) return { cidade: divisao[1].trim(), uf };
  }
  return { cidade: municipio.trim(), uf: ufSeparada };
}

const DOMINIOS = 'gmail|hotmail|outlook|yahoo|live|icloud|bol|uol|terra|msn|ig|globo|me';

// Corrige o que o OCR costuma errar no e-mail: o "@" lido como "(o", "(d", "Q", "©"... e o ponto lido como espaço
function corrigirEmail(bruto) {
  let email = (bruto || '').trim();
  let corrigido = false;
  if (!email.includes('@')) {
    const trocaArroba = new RegExp(`^([A-Za-z0-9._%+-]+?)(?:[(\\[{©®Q@][a-z]?|\\s)(${DOMINIOS})\\b`);
    if (trocaArroba.test(email)) {
      email = email.replace(trocaArroba, '$1@$2');
      corrigido = true;
    }
  }
  const semEspacos = email.replace(/\s+(com|br|net|org)\b/gi, '.$1').replace(/\s+/g, '').replace(/[,;]$/, '');
  if (semEspacos !== email) corrigido = true;
  email = semEspacos.toLowerCase();
  return { email, corrigido };
}

function extrairEmail(linhas, texto) {
  const doRotulo = valorDoRotulo(linhas, ROTULOS.email);
  const bruto = doRotulo || (texto.match(/\S+@\S+/) || [''])[0];
  if (!bruto) return { email: '', duvida: false };
  const { email, corrigido } = corrigirEmail(bruto);
  const valido = /^[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}$/.test(email);
  return { email, duvida: corrigido || !valido };
}

function extrairTelefone(linhas) {
  const numeros = corrigirDigitos(valorDoRotulo(linhas, ROTULOS.telefone)).replace(/\D/g, '').replace(/^0/, '');
  if (numeros.length === 11) return `(${numeros.slice(0, 2)}) ${numeros.slice(2, 7)}-${numeros.slice(7)}`;
  if (numeros.length === 10) return `(${numeros.slice(0, 2)}) ${numeros.slice(2, 6)}-${numeros.slice(6)}`;
  return '';
}

function extrairCep(linhas, texto) {
  const doRotulo = corrigirDigitos(valorDoRotulo(linhas, ROTULOS.cep)).match(/\d{5}-?\d{3}/);
  const qualquer = texto.match(/\b\d{5}-\d{3}\b/);
  const cep = ((doRotulo || qualquer || [''])[0]).replace(/\D/g, '');
  return cep.length === 8 ? `${cep.slice(0, 5)}-${cep.slice(5)}` : '';
}

const formatarCpf = (c) => `${c.slice(0, 3)}.${c.slice(3, 6)}.${c.slice(6, 9)}-${c.slice(9)}`;
const formatarCnpj = (c) => `${c.slice(0, 2)}.${c.slice(2, 5)}.${c.slice(5, 8)}/${c.slice(8, 12)}-${c.slice(12)}`;

// Devolve { dados, duvidas }: dados só com os campos encontrados (nome, cpf ou cnpj, email, telefone, cep, cidade, uf,
// logradouro, numero, complemento, bairro), já no formato das máscaras da tela; duvidas = campos para conferir com atenção.
export function extrairClienteDeTexto(texto) {
  const original = texto || '';
  const linhas = original.split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
  const dados = {};
  const duvidas = [];

  const nome = extrairNome(linhas);
  if (nome.length >= 3) dados.nome = nome;

  const documento = extrairDocumento(linhas, original);
  if (documento.numero.length === 11) dados.cpf = formatarCpf(documento.numero);
  if (documento.numero.length === 14) dados.cnpj = formatarCnpj(documento.numero);
  if (documento.duvida) duvidas.push(documento.numero.length === 11 ? 'cpf' : 'cnpj');

  const { email, duvida: emailDuvidoso } = extrairEmail(linhas, original);
  if (email) {
    dados.email = email;
    if (emailDuvidoso) duvidas.push('email');
  }

  const telefone = extrairTelefone(linhas);
  if (telefone) dados.telefone = telefone;

  const cep = extrairCep(linhas, original);
  if (cep) dados.cep = cep;

  const { cidade, uf } = extrairCidadeUf(linhas);
  if (cidade) dados.cidade = cidade;
  if (uf) dados.uf = uf;

  const endereco = separarEndereco(valorDoRotulo(linhas, ROTULOS.endereco, true));
  Object.assign(dados, endereco);
  const bairro = valorDoRotulo(linhas, ROTULOS.bairro);
  if (bairro) dados.bairro = bairro;

  return { dados, duvidas };
}
